<template>
  <div class="change-password-page">
    <el-card class="change-password-card">
      <template #header>
        <div class="card-header">
          <span>修改密码</span>
          <el-tag v-if="mustChange" type="warning">首次登录需修改密码</el-tag>
        </div>
      </template>

      <el-alert
        :title="PASSWORD_POLICY_HINT"
        type="info"
        :closable="false"
        style="margin-bottom: 16px"
      />

      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="旧密码" prop="old_password">
          <el-input v-model="form.old_password" type="password" show-password />
        </el-form-item>
        <el-form-item label="新密码" prop="new_password">
          <el-input v-model="form.new_password" type="password" show-password />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirm_password">
          <el-input v-model="form.confirm_password" type="password" show-password />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="submitting" @click="submit">
            保存新密码
          </el-button>
          <el-button @click="goBack" :disabled="mustChange">返回</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <SiteBeianFooter />
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import request from '@/utils/request'
import { resolvePostLoginPath } from '@/utils/device'
import { PASSWORD_POLICY_HINT, validatePasswordClient } from '@/utils/passwordPolicy'
import SiteBeianFooter from '@/components/SiteBeianFooter.vue'

const router = useRouter()
const formRef = ref(null)
const submitting = ref(false)

const currentUser = computed(() => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')
  } catch (_) {
    return {}
  }
})

const mustChange = computed(() => !!currentUser.value?.must_change_password)

const form = reactive({
  old_password: '',
  new_password: '',
  confirm_password: ''
})

const rules = {
  old_password: [{ required: true, message: '请输入旧密码', trigger: 'blur' }],
  new_password: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        const err = validatePasswordClient(value, currentUser.value?.username)
        if (err) callback(new Error(err))
        else callback()
      },
      trigger: 'blur'
    }
  ],
  confirm_password: [
    { required: true, message: '请确认新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== form.new_password) {
          callback(new Error('两次输入的新密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ]
}

const submit = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    submitting.value = true
    try {
      const response = await request({
        url: '/api/auth/change-password',
        method: 'post',
        data: {
          old_password: form.old_password,
          new_password: form.new_password
        }
      })
      if (response.success) {
        const updatedUser = { ...currentUser.value, must_change_password: false }
        localStorage.setItem('user', JSON.stringify(updatedUser))
        ElMessage.success('密码修改成功，请继续使用系统')
        router.replace(resolvePostLoginPath(null, currentUser.value))
      } else {
        ElMessage.error(response.message || '修改密码失败')
      }
    } catch (err) {
      ElMessage.error(err.response?.data?.message || '修改密码失败')
    } finally {
      submitting.value = false
    }
  })
}

const goBack = () => router.back()
</script>

<style scoped>
.change-password-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
}

.change-password-page .change-password-card {
  flex: 1;
  margin: auto;
}

.change-password-page :deep(.site-beian-footer) {
  margin-top: auto;
  width: 100%;
}
.change-password-card {
  width: 560px;
  max-width: 95vw;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
